import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { gepruefteSpalten, readFormats } from "@/office/excel/formate";
import { nichtPruefbar } from "../schnittstelle";

export const lektionPruefregeln: Lesson = {
  id: "pruefregeln",
  title: "Grenzen, Datumsbereiche und eigene Regeln",
  summary: "Was eine Datenüberprüfung noch kann – und was sie ausdrücklich nicht leistet.",
  durationMinutes: 11,
  learningGoals: [
    "Zahlen- und Datumsgrenzen als Regel hinterlegen",
    "Hilfreiche Eingabe- und Fehlermeldungen schreiben",
    "Die Grenzen der Datenüberprüfung einschätzen",
  ],
  steps: [
    {
      id: "grenzen-setzen",
      title: "Zahlen und Datumswerte eingrenzen",
      explanation: [
        "Neben der Liste kennt der Dialog weitere Regelarten. „Ganze Zahl“ und " +
          "„Dezimal“ begrenzen den Wertebereich – etwa eine Menge zwischen 1 und 1000. " +
          "Das fängt den Tippfehler ab, bei dem eine Null zu viel ins Feld rutscht: " +
          "10000 statt 1000 wird gar nicht erst angenommen.",
        "„Datum“ und „Uhrzeit“ tun dasselbe für Termine. Eine Regel „Datum zwischen " +
          "1.1.2024 und HEUTE()“ verhindert sowohl Eingaben aus dem letzten " +
          "Jahrtausend als auch versehentliche Zukunftsdaten – beides typische Folgen " +
          "eines vertippten Jahres.",
        "„Textlänge“ begrenzt die Zahl der Zeichen. Nützlich für Kürzel und " +
          "Nummern mit fester Länge, etwa eine vierstellige Kostenstelle. Und " +
          "„Benutzerdefiniert“ nimmt eine beliebige Formel entgegen, die WAHR oder " +
          "FALSCH ergeben muss – damit lässt sich praktisch jede Bedingung abbilden, " +
          "etwa dass ein Enddatum nach dem Anfangsdatum liegen muss.",
        "Auch hier gilt die Regel aus dem Formelmodul: Die Grenzen gehören nicht in " +
          "den Dialog getippt, sondern in Zellen, auf die die Regel verweist. Sonst " +
          "steckt der zulässige Höchstbetrag unsichtbar in einer Einstellung, die " +
          "niemand öffnet.",
      ],
      keyPoints: [
        "Ganze Zahl, Dezimal, Datum, Uhrzeit, Textlänge, Benutzerdefiniert.",
        "Eine Formel bei „Benutzerdefiniert“ muss WAHR oder FALSCH ergeben.",
        "Grenzwerte auf das Blatt legen und darauf verweisen, nicht eintippen.",
      ],
      task: {
        instruction:
          "Ergänze für eine deiner Zahlen- oder Datumsspalten eine Datenüberprüfung, " +
          "die den zulässigen Bereich eingrenzt.",
        hint: "Spalte markieren, Daten → Datenüberprüfung → Zulassen: Ganze Zahl, Operator „zwischen“.",
        check: async () => {
          const formate = await readFormats();
          if (formate.validations === null) {
            return nichtPruefbar(
              "Die Datenüberprüfung eines Bereichs",
              "1.8",
              "Öffne Daten → Datenüberprüfung auf der Spalte und sieh nach, was unter „Zulassen“ steht.",
            );
          }

          const grenzregeln = gepruefteSpalten(formate).filter((eintrag) =>
            ["WholeNumber", "Decimal", "Date", "Time", "TextLength"].includes(eintrag.type),
          );

          if (grenzregeln.length === 0) {
            const vorhandene = gepruefteSpalten(formate);
            return fail(
              vorhandene.length > 0
                ? `Es gibt Datenüberprüfungen vom Typ ${[...new Set(vorhandene.map((eintrag) => eintrag.type))].join(", ")}, ` +
                  "aber keine, die einen Zahlen- oder Datumsbereich eingrenzt."
                : "In der Arbeitsmappe ist noch keine Datenüberprüfung hinterlegt.",
              "Markiere eine Zahlenspalte und wähle Daten → Datenüberprüfung → Zulassen: Ganze Zahl, " +
                "Daten: zwischen, dann Minimum und Maximum eintragen.",
            );
          }

          const erste = grenzregeln[0];
          const grenzen =
            erste.formula1 !== null
              ? ` (${erste.operator ?? "Vergleich"}: ${erste.formula1}${erste.formula2 ? ` bis ${erste.formula2}` : ""})`
              : "";
          return pass(
            `Auf „${erste.sheetName}“ begrenzt der Bereich ${erste.address} die zulässigen Werte ` +
              `über eine Regel vom Typ „${erste.type}“${grenzen}.`,
          );
        },
      },
    },
    {
      id: "meldungen",
      title: "Meldungen, die weiterhelfen",
      explanation: [
        "Die Standardfehlermeldung von Excel lautet sinngemäß: „Der eingegebene Wert " +
          "ist ungültig.“ Das ist zutreffend und vollkommen nutzlos – es sagt nicht, " +
          "was gilt und was stattdessen einzutragen wäre.",
        "Auf der Registerkarte „Fehlermeldung“ lässt sich ein eigener Text " +
          "hinterlegen. Gut ist er, wenn er die Regel nennt und den nächsten Schritt: " +
          "„Die Menge muss zwischen 1 und 1000 liegen. Größere Bestellungen bitte auf " +
          "mehrere Zeilen aufteilen.“",
        "Wichtiger als der Text ist dort das Klappfeld „Typ“. „Stopp“ verweigert die " +
          "Eingabe vollständig. „Warnung“ fragt nach und lässt den Wert auf Wunsch " +
          "zu. „Information“ meldet nur und nimmt den Wert an. Für harte Regeln ist " +
          "Stopp richtig, für Plausibilitätsprüfungen mit begründeten Ausnahmen die " +
          "Warnung.",
        "Die dritte Registerkarte „Eingabemeldung“ wird selten benutzt und ist die " +
          "freundlichste von allen: Sie zeigt einen Hinweis, sobald jemand die Zelle " +
          "anklickt – also bevor etwas schiefgehen kann. Ein Satz wie „Datum im " +
          "Format TT.MM.JJJJ“ erspart die Fehlermeldung ganz.",
      ],
      keyPoints: [
        "Fehlermeldung → Typ: Stopp verweigert, Warnung fragt nach, Information meldet nur.",
        "Ein guter Meldungstext nennt die Regel und den nächsten Schritt.",
        "Die Eingabemeldung erscheint vorher – besser als jede Fehlermeldung.",
      ],
    },
    {
      id: "grenzen-der-pruefung",
      title: "Was die Datenüberprüfung nicht kann",
      explanation: [
        "Eine Datenüberprüfung greift nur bei der Eingabe über die Tastatur. Beim " +
          "Einfügen aus der Zwischenablage greift sie nicht – Strg + V überschreibt " +
          "die Regel sogar, weil das Einfügen die Zellformatierung samt " +
          "Datenüberprüfung mitbringt. In einer Datei, die viele Leute befüllen, ist " +
          "das die häufigste Ursache dafür, dass eine sorgfältig eingerichtete Prüfung " +
          "plötzlich weg ist.",
        "Dagegen hilft nur eines: „Inhalte einfügen“ → „Werte“ statt des normalen " +
          "Einfügens. Erzwingen lässt sich das nicht, aber die Regel überlebt " +
          "immerhin, und man kann darauf hinweisen.",
        "Ebenfalls nicht erfasst sind Werte, die schon vor dem Anlegen der Regel in " +
          "den Zellen standen. Excel prüft rückwirkend nichts. Es gibt dafür aber ein " +
          "Werkzeug: „Daten“ → „Datenüberprüfung“ → „Ungültige Daten einkreisen“ " +
          "markiert alle Zellen, die gegen ihre Regel verstoßen, mit einem roten Oval. " +
          "Nach dem Nachträglich-Einrichten einer Regel lohnt sich dieser Klick immer.",
        "Und schließlich: Eine Datenüberprüfung ist kein Schutz. Sie lässt sich " +
          "jederzeit über denselben Dialog entfernen. Wer verhindern will, dass jemand " +
          "eine Zelle ändert, braucht den Blattschutz – das Thema in zwei Modulen.",
      ],
      keyPoints: [
        "Einfügen mit Strg + V umgeht die Prüfung und überschreibt sie sogar.",
        "„Ungültige Daten einkreisen“ prüft vorhandene Werte nachträglich.",
        "Eine Datenüberprüfung ist eine Hilfe, kein Schutz.",
      ],
      task: {
        instruction:
          "Prüfe mit „Ungültige Daten einkreisen“, ob in deinen geprüften Spalten noch " +
          "Werte stehen, die gegen ihre eigene Regel verstoßen – und bereinige sie.",
        hint: "Daten → Datenüberprüfung (Pfeil darunter) → Ungültige Daten einkreisen. Die roten Ovale verschwinden, sobald der Wert passt.",
        check: async () => {
          const formate = await readFormats();
          if (formate.validations === null) {
            return nichtPruefbar(
              "Die Gültigkeit der Werte in einem geprüften Bereich",
              "1.8",
              "Wähle Daten → Datenüberprüfung → „Ungültige Daten einkreisen“ und sieh nach, ob rote Ovale erscheinen.",
            );
          }

          const geprueft = gepruefteSpalten(formate);
          if (geprueft.length === 0) {
            return fail(
              "In der Arbeitsmappe ist noch keine Datenüberprüfung hinterlegt.",
              "Löse zuerst die Aufgaben der vorigen Schritte.",
            );
          }

          const ungueltig = geprueft.filter((eintrag) => eintrag.valid === false);
          if (ungueltig.length > 0) {
            const erste = ungueltig[0];
            return fail(
              `Im Bereich ${erste.address} auf „${erste.sheetName}“ stehen Werte, die gegen die ` +
                `hinterlegte Regel („${erste.type}“) verstoßen. Excel prüft beim Anlegen einer Regel ` +
                "nicht rückwirkend – solche Werte bleiben unbeanstandet stehen.",
              "Wähle Daten → Datenüberprüfung → „Ungültige Daten einkreisen“. Die betroffenen Zellen " +
                "bekommen ein rotes Oval; korrigiere sie und der Kreis verschwindet.",
            );
          }

          return pass(
            `Alle ${geprueft.length} geprüften Bereiche enthalten ausschließlich zulässige Werte – ` +
              "auch die, die schon vor dem Anlegen der Regel dort standen.",
          );
        },
      },
    },
  ],
};
